// First-person walker for WORLD COSTA BLANCA.
//
// SOURCE REPO: Juanmaes83/sakura-crossing
// SOURCE PATH: src/core/player.js
// SOURCE COMMIT: donors clone 2026-08-17 (public master HEAD)
// CAPABILITY: pointer-lock look, accelerated WASD, axis-separated AABB
//   collision push-out, sub-stepped movement, terrain height follow with
//   step-up, head bob, interactable raycast pick.
// METHOD: REPURPOSE
// CHANGES MADE:
//   - removed spherical-planet coupling (planet.js R/basisAt/positionAt/wrapX);
//     this walker is flat-world (x, z) with a plain yaw/pitch camera frame;
//   - removed ride/mount system (RIDE, nose probe, banking);
//   - removed z-wrap; replaced with rectangular world bounds;
//   - exposes pose() for the CameraAuthority instead of writing a THREE camera;
//   - interaction pick stays, but the camera pose comes from the authority.
// WHY: the product needs Sakura-grade walking feel behind a single camera
//   writer per frame (IW camera-authority), not a second camera owner.
// DEPENDENCIES: none (no three import; pure math + DOM events).
// RISKS: collision quality depends on builders registering honest AABBs.

export const WALKER = Object.freeze({
  EYE: 1.62,
  RADIUS: 0.34,
  STEP: 0.38,
  WALK_SPEED: 2.55,
  RUN_SPEED: 5.1,
  SENSITIVITY: 0.0022,
  PITCH_MIN: -1.15,
  PITCH_MAX: 1.05
});

const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

export class Walker {
  /**
   * @param {HTMLElement} dom pointer-lock element
   * @param {{colliders:Array, heightAt:(x:number,z:number,feetY?:number)=>number, bounds:{x0:number,x1:number,z0:number,z1:number}}} world
   */
  constructor(dom, world, opts = {}) {
    this.dom = dom;
    this.world = world;
    this.spawn = {
      x: opts.x ?? 0, z: opts.z ?? 0,
      yaw: opts.yaw ?? 0, pitch: opts.pitch ?? 0
    };
    this.pos = { x: this.spawn.x, y: 0, z: this.spawn.z };
    this.pos.y = world.heightAt(this.pos.x, this.pos.z);
    this.yaw = this.spawn.yaw;
    this.pitch = this.spawn.pitch;
    this.vel = { x: 0, z: 0 };
    this.bob = 0;
    this._moving = 0;
    this.locked = false;
    this.enabled = false; // set by the camera authority owner lifecycle
    this.keys = new Set();
    this.onInteract = null;
    this.hovered = null;
    this._bind();
  }

  _bind() {
    document.addEventListener('mousemove', (e) => {
      if (!this.locked || !this.enabled) return;
      this.yaw -= e.movementX * WALKER.SENSITIVITY;
      this.pitch = clamp(this.pitch - e.movementY * WALKER.SENSITIVITY, WALKER.PITCH_MIN, WALKER.PITCH_MAX);
    });
    document.addEventListener('pointerlockchange', () => {
      this.locked = document.pointerLockElement === this.dom;
      if (!this.locked) this.keys.clear();
    });
    window.addEventListener('keydown', (e) => {
      if (e.repeat) return;
      this.keys.add(e.code);
      if (e.code === 'KeyE' && this.locked && this.enabled) this.onInteract?.(this.hovered);
      if (['KeyW', 'KeyA', 'KeyS', 'KeyD', 'Space'].includes(e.code) && this.locked && this.enabled) e.preventDefault();
    });
    window.addEventListener('keyup', (e) => this.keys.delete(e.code));
    window.addEventListener('blur', () => this.keys.clear());
  }

  lock() { this.dom.requestPointerLock?.(); }

  /** Adopt an external pose (authority handoff / return contract). */
  setPose({ x, z, yaw, pitch }) {
    if (Number.isFinite(x)) this.pos.x = x;
    if (Number.isFinite(z)) this.pos.z = z;
    if (Number.isFinite(yaw)) this.yaw = yaw;
    if (Number.isFinite(pitch)) this.pitch = pitch;
    this.vel.x = 0;
    this.vel.z = 0;
  }

  /** Push the walker disc out of any collider it overlaps, one axis at a time. */
  _resolve(feetY) {
    const p = this.pos;
    const r = WALKER.RADIUS;
    for (const c of this.world.colliders) {
      if (c.top !== undefined && c.top <= feetY + WALKER.STEP) continue;
      if (c.bottom !== undefined && c.bottom > feetY + 1.9) continue;
      const x0 = c.x0 - r, x1 = c.x1 + r;
      const z0 = c.z0 - r, z1 = c.z1 + r;
      if (p.x <= x0 || p.x >= x1 || p.z <= z0 || p.z >= z1) continue;
      const dxL = p.x - x0, dxR = x1 - p.x;
      const dzL = p.z - z0, dzR = z1 - p.z;
      const m = Math.min(dxL, dxR, dzL, dzR);
      if (m === dxL) p.x = x0;
      else if (m === dxR) p.x = x1;
      else if (m === dzL) p.z = z0;
      else p.z = z1;
    }
  }

  update(dt) {
    const k = this.keys;
    const active = this.locked && this.enabled;
    const sprint = k.has('ShiftLeft') || k.has('ShiftRight');
    const speed = sprint ? WALKER.RUN_SPEED : WALKER.WALK_SPEED;

    let fwd = 0, side = 0;
    if (active) {
      if (k.has('KeyW') || k.has('ArrowUp')) fwd += 1;
      if (k.has('KeyS') || k.has('ArrowDown')) fwd -= 1;
      if (k.has('KeyD') || k.has('ArrowRight')) side += 1;
      if (k.has('KeyA') || k.has('ArrowLeft')) side -= 1;
    }

    const fx = -Math.sin(this.yaw), fz = -Math.cos(this.yaw);
    const rx = Math.cos(this.yaw), rz = -Math.sin(this.yaw);
    let wx = fx * fwd + rx * side;
    let wz = fz * fwd + rz * side;
    const wl = Math.hypot(wx, wz);
    if (wl > 1e-6) { wx = (wx / wl) * speed; wz = (wz / wl) * speed; } else { wx = 0; wz = 0; }

    // Critically-damped approach to wish velocity (sakura: accel 13 / brake 16).
    const accel = wl > 1e-6 ? 13 : 16;
    const a = 1 - Math.exp(-accel * dt);
    this.vel.x += (wx - this.vel.x) * a;
    this.vel.z += (wz - this.vel.z) * a;

    const feetY = this.world.heightAt(this.pos.x, this.pos.z, this.pos.y);

    // Sub-step so sprinting cannot tunnel through thin walls.
    const stepX = this.vel.x * dt, stepZ = this.vel.z * dt;
    const n = Math.max(1, Math.ceil(Math.max(Math.abs(stepX), Math.abs(stepZ)) / 0.18));
    for (let i = 0; i < n; i++) {
      this.pos.x += stepX / n;
      this._resolve(feetY);
      this.pos.z += stepZ / n;
      this._resolve(feetY);
    }

    const b = this.world.bounds;
    this.pos.x = clamp(this.pos.x, b.x0, b.x1);
    this.pos.z = clamp(this.pos.z, b.z0, b.z1);

    const targetY = this.world.heightAt(this.pos.x, this.pos.z, this.pos.y);
    this.pos.y += (targetY - this.pos.y) * (1 - Math.exp(-18 * dt));

    const moving = Math.hypot(this.vel.x, this.vel.z);
    this.bob += dt * moving * (sprint ? 8.2 : 6.4);
    this._moving = moving;
  }

  /** CameraAuthority pose: eye position + look target from yaw/pitch. */
  pose() {
    const amp = Math.min(this._moving / WALKER.WALK_SPEED, 1) * 0.014;
    const eye = this.pos.y + WALKER.EYE + Math.sin(this.bob) * amp;
    const cp = Math.cos(this.pitch);
    const dx = -Math.sin(this.yaw) * cp;
    const dy = Math.sin(this.pitch);
    const dz = -Math.cos(this.yaw) * cp;
    return {
      position: [this.pos.x, eye, this.pos.z],
      target: [this.pos.x + dx, eye + dy, this.pos.z + dz],
      fov: 55
    };
  }
}
